// Keeps the load boards' filters and localStorage in step: restores them once the signed-in
// project is known, then saves every later change.
//
// It runs from the store rather than from a hook on each page so the filters are already restored
// by the time either board mounts and fires its first fetch — a hook would restore them one render
// too late and the board would fetch unfiltered first.
//
// Both boards are driven through their BoardFilterSpec, so adding a filter to one is a line in
// boardFilterSpecs and nothing here.

import type { Store, Unsubscribe } from '@reduxjs/toolkit'
import { isEqual } from 'lodash'

import { DEFAULT_BOARD_STATE, DISPATCH_BOARD, ONGOING_LOADS_BOARD, type BoardFilterSpec, type BoardFilterState } from './boardFilterSpecs'
import { readStoredBoardFilters, scopeKey, writeStoredBoardFilters, type BoardFilterScope, type StoredBoardFilters } from './boardFilterStorage'
import type { RootState } from './index'

/** The store slices this needs: the boards' filters, plus auth to know whose filters they are. */
type BoardFilterSyncState = BoardFilterState & Pick<RootState, 'auth'>

/** Every filter of one board, by name — the working shape, before defaults are dropped. */
type BoardFilterValues = Record<string, unknown>

const BOARDS: BoardFilterSpec[] = [ONGOING_LOADS_BOARD, DISPATCH_BOARD]

/** Whose filters these are, or null while signed out — the trigger for a restore. */
const currentScope = (state: BoardFilterSyncState): BoardFilterScope | null => {
    const projectName = state.auth.application?.projectName
    const userId = state.auth.user?.id
    return projectName && userId ? { projectName, userId } : null
}

const readBoard = (spec: BoardFilterSpec, state: BoardFilterState): BoardFilterValues =>
    Object.fromEntries(spec.fields.map((filter) => [filter.name, filter.read(state)]))

const restoreBoard = (spec: BoardFilterSpec, store: Store<BoardFilterSyncState>, stored: StoredBoardFilters): void =>
    spec.fields.forEach((filter) => filter.restore(stored[filter.name], store.dispatch))

/**
 * Compared by reference: Immer keeps a slice field's identity when a reducer leaves it alone, so
 * this is an accurate "did a filter change" test at O(fields) — cheap enough to run on every action
 * in a store that also carries load and websocket traffic. The key sets are fixed by the spec, so
 * comparing values covers it.
 */
const hasChanged = (before: BoardFilterValues, after: BoardFilterValues): boolean => Object.keys(after).some((name) => before[name] !== after[name])

/**
 * Narrows a board down to what the user actually chose. A filter still sitting at its board default
 * carries no information: storing it would copy the defaults under every company the user has ever
 * signed into, and go stale the day a default changes. An empty result drops the entry entirely —
 * clearing your filters gives the key back.
 *
 * Unlike the change check above this compares by value, since a default and a current filter never
 * share an identity. It only runs when something has actually changed, not on every action.
 */
const withoutDefaults = (spec: BoardFilterSpec, values: BoardFilterValues): StoredBoardFilters =>
    Object.fromEntries(
        spec.fields.filter((filter) => !isEqual(values[filter.name], filter.read(DEFAULT_BOARD_STATE))).map((filter) => [filter.name, values[filter.name]])
    )

/**
 * Starts restoring and saving the boards' filters. Returns the store unsubscribe so tests (and any
 * future teardown) can stop it.
 */
export const attachBoardFilterPersistence = (store: Store<BoardFilterSyncState>): Unsubscribe => {
    const lastSeen = new Map<BoardFilterSpec, BoardFilterValues>()
    let restoredKey: string | null = null
    // Restoring dispatches, which re-enters this subscriber; skip those passes rather than reading
    // half-restored state back out and saving it.
    let restoring = false

    const restoreAll = (scope: BoardFilterScope) => {
        restoring = true
        try {
            BOARDS.forEach((spec) => restoreBoard(spec, store, readStoredBoardFilters(spec, scope)))
        } finally {
            restoring = false
        }
        restoredKey = scopeKey(scope)
        BOARDS.forEach((spec) => lastSeen.set(spec, readBoard(spec, store.getState())))
    }

    const saveChanged = (scope: BoardFilterScope) => {
        const state = store.getState()
        BOARDS.forEach((spec) => {
            const current = readBoard(spec, state)
            const previous = lastSeen.get(spec)
            if (previous && !hasChanged(previous, current)) return
            lastSeen.set(spec, current)
            writeStoredBoardFilters(spec, scope, withoutDefaults(spec, current))
        })
    }

    const sync = () => {
        if (restoring) return

        const scope = currentScope(store.getState())

        // Signed out (or a half-hydrated session, where the user is known but the company is not
        // yet). Logging out only resets the auth slice and navigates — this store, these closures
        // and both boards' filters all survive it — so forget what is loaded: whoever signs in next
        // has to get a fresh restore, or they inherit the previous dispatcher's filters.
        if (!scope) {
            restoredKey = null
            lastSeen.clear()
            return
        }

        // A different user or company means a fresh restore — on boot, after a sign-in, on a
        // company switch, and on a handover to the next dispatcher at the same terminal.
        if (scopeKey(scope) !== restoredKey) restoreAll(scope)
        else saveChanged(scope)
    }

    // The session may already be restored by the time the store is built.
    sync()

    return store.subscribe(sync)
}
